import { Box, Chip, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { useDeleteSalaryEntry, useSalaryData } from 'entities/salary/salary-entries/salary-entries.hook';
import { ADDITION_TYPES, DEDUCTION_TYPES, SALARY_ENTRY_TYPES, SalaryEntryDto, salaryTypeLabels } from '@sr/dto';
import { GridLayout, HeadCellsCollection } from 'shared/ui/grid-layout';
import { formatDateTime } from 'utils/date-format-helpers';
import { formatPersonPruning } from 'utils/person-format-helpers';
import PriceSpan from 'shared/ui/price-span';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnack } from 'shared/ui/snack';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import EditIcon from '@mui/icons-material/Edit';
import { useDeletePrepayment } from '../prepayment/prepayment.hook';
import { useMemo, useState } from 'react';

const columns: HeadCellsCollection = [
  { label: '№', align: 'left' },
  { label: 'Что', align: 'left' },
  { label: 'Тип', align: 'left' },
  { label: 'Кто начислил', align: 'left' },
  { label: 'Дата начисления', align: 'left' },
  { label: 'Сумма', align: 'right' },
  { label: 'Комментарий', align: 'left' },
  { label: 'Действия', align: 'right' },
];

const isAddition = (type: SALARY_ENTRY_TYPES) => ADDITION_TYPES.includes(type);

type SalarySortMode = 'default' | 'prepayment' | 'addition' | 'deduction' | 'other' | SALARY_ENTRY_TYPES;

const sortOptions: { value: SalarySortMode; label: string }[] = [
  { value: 'default', label: 'Все' },
  { value: 'prepayment', label: 'Аванс' },
  { value: 'addition', label: 'Начисления' },
  { value: 'deduction', label: 'Удержания' },
];
const otherSortOption: { value: SalarySortMode; label: string } = { value: 'other', label: 'Остальные' };

const getSortGroup = (row: SalaryEntryDto): SalarySortMode => {
  if (row.type === SALARY_ENTRY_TYPES.PREPAYMENT) return 'prepayment';
  if (ADDITION_TYPES.includes(row.type)) return 'addition';
  if (DEDUCTION_TYPES.includes(row.type)) return 'deduction';
  return 'other';
};

const applySortMode = (rows: SalaryEntryDto[], sortMode: SalarySortMode) => {
  if (sortMode === 'default') return rows;
  return [...rows].sort((a, b) => {
    const aPriority = (a.type === sortMode || getSortGroup(a) === sortMode) ? 0 : 1;
    const bPriority = (b.type === sortMode || getSortGroup(b) === sortMode) ? 0 : 1;
    if (aPriority !== bPriority) return aPriority - bPriority;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });
};

const SignedMoney = ({ value, size }: { value: number; size?: 'small' | 'regular' }) => {
  const negative = value < 0;
  const color = negative ? 'error.main' : 'success.main';
  return (
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      <Typography component="span" color={color} fontWeight={500}>{negative ? '−' : '+'} </Typography>
      <PriceSpan price={Math.abs(value)} color={color} size={size} />
    </Box>
  );
};

const SalaryRow = ({ row, index, onDeleteClick, onEditClick }: {
  row: SalaryEntryDto,
  index: number,
  onDeleteClick: (row: SalaryEntryDto) => Promise<void>,
  onEditClick: (salaryEntryId: number)=>void,
}) => {
  const addition = isAddition(row.type);
  const signedSum = addition ? row.sum : -row.sum;

  return (
    <TableRow hover>
      <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
      <TableCell>
        <Chip
          size="small"
          variant="outlined"
          color={addition ? 'success' : 'error'}
          label={addition ? 'Начисление' : 'Удержание'}
        />
      </TableCell>
      <TableCell>
        {salaryTypeLabels[row.type] ?? '-- неизвестный тип --'}
      </TableCell>
      <TableCell>
        {formatPersonPruning(row.createdBy)}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
        {formatDateTime(row.createdAt)}
      </TableCell>
      <TableCell align="right">
        <SignedMoney value={signedSum} size="small" />
      </TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>
        {row.description}
      </TableCell>
      <TableCell align="right">
        {row.type === SALARY_ENTRY_TYPES.PREPAYMENT
          && <IconButton size="small" onClick={() => onEditClick(row.id)}>
            <EditIcon fontSize="small" />
          </IconButton>}
        <IconButton size="small" onClick={() => onDeleteClick(row)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>);
};

const SalaryTotal = (rows: SalaryEntryDto[]) => {
  const total = rows.reduce<number>((acc, row) => {
    if (ADDITION_TYPES.includes(row.type)) {
      return acc + row.sum;
    }
    if (DEDUCTION_TYPES.includes(row.type)) {
      return acc - row.sum;
    }
    throw new Error('Расчет общей суммы зарплаты невозможен. Неизвестный тип.');
  }, 0);
  return (
    <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', backgroundColor: 'action.hover' } }}>
      <TableCell>
        <Typography variant="subtitle1" fontWeight={600}>Итого:</Typography>
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell align="right">
        <SignedMoney value={total} size="regular" />
      </TableCell>
      <TableCell />
      <TableCell />
    </TableRow>
  );
};

export const SalaryEntriesGrid = ({ year, month, userId, onEditClick }: {
  year: number,
  month: number,
  userId: number,
  onEditClick: (salaryEntryId: number)=> void
}) => {
  const { rows, isLoading, error } = useSalaryData(year, month, userId);
  const deleteSalaryEntry = useDeleteSalaryEntry(year, month, userId);
  const deletePrepayment = useDeletePrepayment({ year, month, userId });
  const { showSuccess, showError } = useSnack();
  const [sortMode, setSortMode] = useState<SalarySortMode>('default');
  const visibleRows = rows.filter((row) => row.type !== SALARY_ENTRY_TYPES.COMMISSION);
  const sortedRows = useMemo(() => applySortMode(visibleRows, sortMode), [sortMode, visibleRows]);
  const typeSortOptions = useMemo(
    () => Array
      .from(new Set(visibleRows.map((row) => row.type)))
      .filter((type) => type !== SALARY_ENTRY_TYPES.PREPAYMENT)
      .map((type) => ({ value: type, label: salaryTypeLabels[type] ?? type })),
    [visibleRows],
  );
  const allSortOptions = useMemo(
    () => [...sortOptions, ...typeSortOptions, otherSortOption],
    [typeSortOptions],
  );

  const handleRowDelete = async (row: SalaryEntryDto) => {

    if (row.type === SALARY_ENTRY_TYPES.PREPAYMENT) {
      await deletePrepayment(row.id)
        .then(() => { showSuccess('Аванс успешно удален') })
        .catch(e => { showError('Ошибка удаления: ' + extractNestJsErrorMessage(e)) });
      return;
    }

    await deleteSalaryEntry(row.id)
      .then(() => { showSuccess('Запись успешно удалена') })
      .catch(e => { showError('Ошибка удаления: ' + extractNestJsErrorMessage(e)) });
  };

  const handleRowEdit = (salaryEntryId: number) => {
    onEditClick(salaryEntryId);
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        {allSortOptions.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            color={sortMode === option.value ? 'primary' : 'default'}
            variant={sortMode === option.value ? 'filled' : 'outlined'}
            onClick={() => setSortMode(option.value)}
            size="small"
          />
        ))}
      </Box>
      <GridLayout
        columns={columns}
        items={sortedRows}
        isLoading={isLoading}
        error={error}
        height="auto"
        footer={SalaryTotal}
        itemRender={(row, index) => <SalaryRow key={row.id} row={row} index={index} onDeleteClick={handleRowDelete}
          onEditClick={handleRowEdit} />}
        noItemsText="У выбраного пользователя нету начислений/удержаний в выбранном периоде" />
    </>
  );
};
